import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Button, Empty, Field, Notice } from '../design-system/components';
import { NewProposal } from '../features/propostas/NewProposal';
import { ProposalWorkspace } from '../features/propostas/ProposalWorkspace';
import { ManageProposal } from '../features/propostas/ManageProposal';
import { GrossupProfileSettings } from '../features/configuracoes/GrossupProfileSettings';
import type { Bootstrap, Proposal } from '../types/contracts';

type View = 'dashboard' | 'new' | 'list' | 'archived' | 'workspace' | 'manage' | 'clients' | 'projects' | 'settings';
function route() {
  const h = location.hash.replace(/^#\/?/, '');
  if (h === 'nova') return { view: 'new' as View };
  if (h === 'propostas') return { view: 'list' as View };
  if (h === 'propostas/arquivadas') return { view: 'archived' as View };
  if (h === 'clientes') return { view: 'clients' as View };
  if (h === 'projetos') return { view: 'projects' as View };
  if (h === 'configuracoes') return { view: 'settings' as View };
  const m = h.match(/^proposta\/([^/]+)(?:\/(gerenciar))?$/);
  if (m) return { view: (m[2] ? 'manage' : 'workspace') as View, id: m[1] };
  return { view: 'dashboard' as View };
}
function go(path: string) {
  location.hash = `#/${path}`;
}
export function App() {
  const [r, setR] = useState(route());
  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const f = () => setR(route());
    addEventListener('hashchange', f);
    api
      .get<Bootstrap>('/bootstrap')
      .then(setBoot)
      .catch((e) => setError(e.message));
    return () => removeEventListener('hashchange', f);
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.setTimeout(() => {
      const title = document.querySelector('main h1, main h2, .flow-head h2') as HTMLElement | null;
      if (title) {
        title.tabIndex = -1;
        title.focus({ preventScroll: true });
      }
    }, 0);
  }, [r.view, r.id]);
  if (error)
    return (
      <main className="page">
        <Notice tone="danger" title="Aplicação indisponível">
          {error}
        </Notice>
      </main>
    );
  if (!boot) return <main className="loading-page">Carregando PropostaZURC...</main>;
  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => go('')}>
          <img src="/logomarca.png" alt="ZURC Engenharia" />
          <span>
            <strong>PropostaZURC</strong>
            <small>Aplicação local</small>
          </span>
        </button>
        <nav aria-label="Navegação principal">
          <button onClick={() => go('')}>Painel</button>
          <button onClick={() => go('propostas')}>Propostas</button>
          <button onClick={() => go('clientes')}>Clientes</button>
          <button onClick={() => go('projetos')}>Projetos</button>
          <button onClick={() => go('configuracoes')}>Configurações</button>
          <Button onClick={() => go('nova')}>Nova proposta</Button>
        </nav>
      </header>
      {!boot.pdf_engine.available && (
        <div className="global-runtime-warning">
          <strong>Emissão em PDF indisponível.</strong> {boot.pdf_engine.message}
        </div>
      )}
      {r.view === 'dashboard' && <Dashboard />}
      {r.view === 'new' && <NewProposal bootstrap={boot} onCreated={(id) => go(`proposta/${id}`)} />}{' '}
      {r.view === 'list' && <ProposalList />}
      {r.view === 'archived' && <ProposalList archived />}
      {r.view === 'workspace' && r.id && (
        <ProposalWorkspace
          id={r.id}
          bootstrap={boot}
          onOpen={(id) => go(`proposta/${id}`)}
          onManage={() => go(`proposta/${r.id}/gerenciar`)}
        />
      )}{' '}
      {r.view === 'manage' && r.id && <ManageProposal id={r.id} onOpen={(id) => go(`proposta/${id}`)} onBack={() => go('propostas')} />}{' '}
      {r.view === 'clients' && <Registry kind="clients" />}
      {r.view === 'projects' && <Registry kind="projects" />}
      {r.view === 'settings' && <Settings />}
    </div>
  );
}
function ProposalRow({ p }: { p: any }) {
  return (
    <article className="proposal-row">
      <div>
        <span className="mono">
          {p.code} · REV {String(p.revision).padStart(2, '0')}
        </span>
        <h3>{p.client_legal_name || p.client_name || 'Cliente pendente'}</h3>
        <p>{p.object_text || p.project_name || 'Objeto pendente'}</p>
      </div>
      <div className="proposal-meta">
        <span>{p.client_person_type || '—'}</span>
        <span>{p.service_code}</span>
        <span>{p.visible_state || p.document_state}</span>
      </div>
      <div>
        <small>Próxima ação</small>
        <strong>{p.next_action || 'Continuar preenchimento'}</strong>
      </div>
      <div className="row-actions">
        <Button variant="secondary" onClick={() => go(`proposta/${p.id}`)}>
          Abrir
        </Button>
        <Button variant="ghost" onClick={() => go(`proposta/${p.id}/gerenciar`)}>
          Gerenciar proposta
        </Button>
      </div>
    </article>
  );
}
function Dashboard() {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    api.get<any>('/dashboard').then(setData);
  }, []);
  return (
    <main className="page dashboard">
      <section className="hero-action">
        <div>
          <span className="eyebrow">CONSTRUIR → EDITAR CONTEÚDO → FINALIZAR PROPOSTA</span>
          <h1>PropostaZURC</h1>
          <p>Crie propostas de forma guiada, revise o conteúdo gerado e finalize o documento.</p>
        </div>
        <Button onClick={() => go('nova')}>Nova proposta</Button>
      </section>
      <DashboardGroup title="Rascunhos em andamento" items={data?.drafts} />
      <DashboardGroup title="Propostas com próxima ação" items={data?.requires_action} />
      <DashboardGroup title="Propostas recentes" items={data?.recent} />
    </main>
  );
}
function DashboardGroup({ title, items }: { title: string; items: any[] }) {
  return (
    <section className="dashboard-group">
      <header>
        <h2>{title}</h2>
        <span>{items?.length || 0}</span>
      </header>
      {items?.length ? (
        items.map((p) => <ProposalRow key={p.id} p={p} />)
      ) : (
        <Empty title="Nenhuma proposta nesta seção" action={<Button onClick={() => go('nova')}>Nova proposta</Button>}>
          O primeiro rascunho aparecerá aqui.
        </Empty>
      )}
    </section>
  );
}
function ProposalList({ archived = false }: { archived?: boolean }) {
  const [items, setItems] = useState<Proposal[]>([]);
  useEffect(() => {
    api.get<Proposal[]>(archived ? '/proposals-archived' : '/proposals').then(setItems);
  }, [archived]);
  return (
    <main className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Propostas</span>
          <h1>{archived ? 'Propostas arquivadas' : 'Propostas, revisões e próximas ações'}</h1>
          {archived && <p>Itens preservados fora da listagem operacional. Abra a gestão da proposta para restaurar.</p>}
        </div>
        <div className="row-actions">
          {archived ? (
            <Button variant="ghost" onClick={() => go('propostas')}>
              Voltar às propostas
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => go('propostas/arquivadas')}>
              Arquivadas
            </Button>
          )}
          {!archived && <Button onClick={() => go('nova')}>Nova proposta</Button>}
        </div>
      </header>
      {items.length ? (
        items.map((p) => <ProposalRow key={p.id} p={p} />)
      ) : (
        <Empty
          title={archived ? 'Nenhuma proposta arquivada' : 'Nenhuma proposta criada'}
          action={archived ? <Button variant="ghost" onClick={() => go('propostas')}>Voltar às propostas</Button> : <Button onClick={() => go('nova')}>Nova proposta</Button>}
        >
          {archived ? 'As propostas arquivadas aparecerão aqui.' : 'Comece pelo cliente, objeto e serviço.'}
        </Empty>
      )}
    </main>
  );
}
function Registry({ kind }: { kind: 'clients' | 'projects' }) {
  const [data, setData] = useState<any[]>([]);
  useEffect(() => {
    api.get<any[]>(`/${kind}`).then(setData);
  }, [kind]);
  return (
    <main className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Cadastros</span>
          <h1>{kind === 'clients' ? 'Clientes' : 'Projetos'}</h1>
          <p>Cadastros podem ser reutilizados na criação de novas propostas.</p>
        </div>
        <Button onClick={() => go('nova')}>Nova proposta</Button>
      </header>
      <div className="registry-table">
        {data.map((x) => (
          <article key={x.id}>
            <div>
              <strong>{x.legal_name || x.name}</strong>
              <span>
                {x.code || x.person_type} · {x.location || x.tax_id_formatted || 'sem complemento'}
              </span>
            </div>
          </article>
        ))}
        {!data.length && <p>Nenhum cadastro.</p>}
      </div>
    </main>
  );
}
function Settings() {
  const [data, setData] = useState<any>(null);
  const [message, setMessage] = useState('');
  useEffect(() => {
    api.get<any>('/settings').then(setData);
  }, []);
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    await api.put('/settings/institutional_data', {
      value: {
        company_name: 'ZURC Engenharia',
        legal_name: String(f.get('legal_name') || ''),
        tax_id: String(f.get('tax_id') || ''),
        address: String(f.get('address') || ''),
        email: String(f.get('email') || ''),
        phone: String(f.get('phone') || ''),
        crea_pj: String(f.get('crea_pj') || ''),
        representative: String(f.get('representative') || ''),
        representative_role: String(f.get('representative_role') || ''),
        slogan: 'Estruturas pensadas para funcionar.',
      },
    });
    setMessage('Dados institucionais salvos.');
  }
  const x = data?.institutional_data || {};
  return (
    <main className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Configurações</span>
          <h1>Dados institucionais</h1>
          <p>Estas informações são utilizadas na finalização e nas assinaturas quando aplicáveis.</p>
        </div>
      </header>
      {message && (
        <Notice tone="success" title="Atualizado">
          {message}
        </Notice>
      )}
      <form className="settings-card" onSubmit={submit}>
        <Field id="legal_name" label="Razão social">
          <input id="legal_name" name="legal_name" defaultValue={x.legal_name} />
        </Field>
        <Field id="tax_id" label="CNPJ">
          <input id="tax_id" name="tax_id" defaultValue={x.tax_id} />
        </Field>
        <Field id="address" label="Endereço">
          <input id="address" name="address" defaultValue={x.address} />
        </Field>
        <Field id="email" label="E-mail">
          <input id="email" name="email" type="email" defaultValue={x.email} />
        </Field>
        <Field id="phone" label="Telefone">
          <input id="phone" name="phone" defaultValue={x.phone} />
        </Field>
        <Field id="crea_pj" label="CREA-BA PJ">
          <input id="crea_pj" name="crea_pj" defaultValue={x.crea_pj} />
        </Field>
        <Field id="representative" label="Representante">
          <input id="representative" name="representative" defaultValue={x.representative} />
        </Field>
        <Field id="representative_role" label="Função">
          <input id="representative_role" name="representative_role" defaultValue={x.representative_role} />
        </Field>
        <Button>Salvar configurações</Button>
      </form>
      <GrossupProfileSettings />
    </main>
  );
}
